/**
 * Skills Matrix Template Form
 * Team skills assessment and gap analysis
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const SkillsMatrixForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    company_name: '',
    department: '',
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    skill_categories: ['Technical', 'Soft Skills', 'Domain'],
    required_skills: [''],
    team_members: [{
      name: '', role: '', department: '', years_experience: 0,
      skills: [{ skill_name: '', category: 'Technical', proficiency_level: 0, target_level: 0, training_needed: '' }],
      certifications: '', training_completed: '', training_planned: '', career_goals: '', notes: ''
    }],
    skill_gaps: [],
    training_recommendations: [''],
    rai_assignments: [],
    followups: []
  });

  const getSampleData = () => ({
    company_name: 'TechCorp Inc.',
    department: 'Engineering',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'Engineering Manager',
    skill_categories: ['Technical', 'Soft Skills', 'Domain', 'Tools'],
    required_skills: ['Python', 'JavaScript/TypeScript', 'AWS', 'Kubernetes', 'SQL', 'Communication', 'Problem Solving', 'Leadership'],
    team_members: [
      {
        name: 'John Smith', role: 'Senior Developer', department: 'Engineering', years_experience: 8,
        skills: [
          { skill_name: 'Python', category: 'Technical', proficiency_level: 5, target_level: 5, training_needed: '' },
          { skill_name: 'AWS', category: 'Technical', proficiency_level: 4, target_level: 5, training_needed: 'AWS Solutions Architect certification' },
          { skill_name: 'Kubernetes', category: 'Technical', proficiency_level: 3, target_level: 4, training_needed: 'CKA certification' },
          { skill_name: 'Leadership', category: 'Soft Skills', proficiency_level: 4, target_level: 5, training_needed: 'Leadership development program' }
        ],
        certifications: 'AWS Developer Associate', training_completed: 'Python Advanced, React Fundamentals',
        training_planned: 'AWS Solutions Architect', career_goals: 'Tech Lead role by 2026', notes: 'Strong mentor'
      },
      {
        name: 'Jane Doe', role: 'Full Stack Developer', department: 'Engineering', years_experience: 5,
        skills: [
          { skill_name: 'JavaScript/TypeScript', category: 'Technical', proficiency_level: 5, target_level: 5, training_needed: '' },
          { skill_name: 'Python', category: 'Technical', proficiency_level: 3, target_level: 4, training_needed: 'Python Advanced course' },
          { skill_name: 'AWS', category: 'Technical', proficiency_level: 2, target_level: 4, training_needed: 'AWS Fundamentals, Hands-on labs' },
          { skill_name: 'Communication', category: 'Soft Skills', proficiency_level: 4, target_level: 5, training_needed: 'Technical writing workshop' }
        ],
        certifications: '', training_completed: 'React Advanced, Node.js Masterclass',
        training_planned: 'AWS Developer Associate', career_goals: 'Senior Developer by 2025', notes: ''
      },
      {
        name: 'Mike Wilson', role: 'DevOps Engineer', department: 'Engineering', years_experience: 6,
        skills: [
          { skill_name: 'Kubernetes', category: 'Technical', proficiency_level: 5, target_level: 5, training_needed: '' },
          { skill_name: 'AWS', category: 'Technical', proficiency_level: 5, target_level: 5, training_needed: '' },
          { skill_name: 'Python', category: 'Technical', proficiency_level: 3, target_level: 4, training_needed: 'Scripting for DevOps course' },
          { skill_name: 'Problem Solving', category: 'Soft Skills', proficiency_level: 5, target_level: 5, training_needed: '' }
        ],
        certifications: 'CKA, AWS Solutions Architect, Terraform Associate', training_completed: 'Kubernetes Security, GitOps',
        training_planned: 'Security certifications', career_goals: 'Platform Engineering Lead', notes: 'Infrastructure expert'
      }
    ],
    skill_gaps: [
      { skill: 'Machine Learning', gap: 'No team member with ML expertise', action: 'Hire or train existing member' },
      { skill: 'Mobile Development', gap: 'Limited React Native experience', action: 'Cross-train frontend developers' }
    ],
    training_recommendations: [
      'Invest in AWS certifications for the team (budget: $5,000)',
      'Leadership development program for senior developers',
      'Monthly knowledge sharing sessions',
      'Consider hiring ML specialist or partnering with consultancy'
    ],
    rai_assignments: [
      { task: 'Complete skills assessment for all team members', responsible: 'Team Leads', accountable: 'Engineering Manager', informed: 'HR, Training Dept', due_date: '2025-01-31', status: 'In Progress' }
    ],
    followups: [
      { item: 'Schedule AWS training sessions', owner: 'Training Coordinator', due_date: '2025-02-15', priority: 'High', status: 'Pending' },
      { item: 'Review career development plans', owner: 'Engineering Manager', due_date: '2025-01-20', priority: 'Medium', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Skills Matrix Draft";
      const payload = {
        name: draftName,
        template_type: "Skills Matrix",
        template_id: "skills-matrix",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "skills-matrix", "Skills Matrix", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("skills-matrix", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/skills-matrix`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Skills_Matrix_${formData.department.replace(/\s+/g, '_') || 'Team'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Skills Matrix",
        formData,
        settings: activeSettings || {},
        templateType: "Skills Matrix",
        templateId: "skills-matrix",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addTeamMember = () => {
    setFormData({
      ...formData,
      team_members: [...formData.team_members, {
        name: '', role: '', department: formData.department, years_experience: 0,
        skills: [{ skill_name: '', category: 'Technical', proficiency_level: 0, target_level: 0, training_needed: '' }],
        certifications: '', training_completed: '', training_planned: '', career_goals: '', notes: ''
      }]
    });
  };

  const updateTeamMember = (index, field, value) => {
    const members = [...formData.team_members];
    members[index] = { ...members[index], [field]: value };
    setFormData({ ...formData, team_members: members });
  };

  const removeTeamMember = (index) => {
    setFormData({ ...formData, team_members: formData.team_members.filter((_, i) => i !== index) });
  };

  const addSkillToMember = (memberIndex) => {
    const members = [...formData.team_members];
    members[memberIndex].skills.push({ skill_name: '', category: 'Technical', proficiency_level: 0, target_level: 0, training_needed: '' });
    setFormData({ ...formData, team_members: members });
  };

  const updateSkill = (memberIndex, skillIndex, field, value) => {
    const members = [...formData.team_members];
    members[memberIndex].skills[skillIndex] = { ...members[memberIndex].skills[skillIndex], [field]: value };
    setFormData({ ...formData, team_members: members });
  };

  const removeSkill = (memberIndex, skillIndex) => {
    const members = [...formData.team_members];
    members[memberIndex].skills = members[memberIndex].skills.filter((_, i) => i !== skillIndex);
    setFormData({ ...formData, team_members: members });
  };

  const proficiencyLevels = [
    { value: 0, label: '0 - None' },
    { value: 1, label: '1 - Beginner' },
    { value: 2, label: '2 - Basic' },
    { value: 3, label: '3 - Intermediate' },
    { value: 4, label: '4 - Advanced' },
    { value: 5, label: '5 - Expert' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Skills Matrix</h1>
                <p className="text-sm text-gray-500">Team skills assessment and gap analysis</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Skills Assessment</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Company *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company" /></div>
              <div><Label>Department *</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} placeholder="Engineering" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Manager" /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Team Members & Skills</Label>
              <p className="text-xs text-gray-500 mb-3">Rate proficiency 0-5: 0=None, 1=Beginner, 2=Basic, 3=Intermediate, 4=Advanced, 5=Expert</p>
              
              <div className="space-y-4">
                {formData.team_members.map((member, mIdx) => (
                  <div key={mIdx} className="p-4 bg-gray-50 rounded-lg border">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm font-medium">Team Member #{mIdx + 1}</span>
                      <Button variant="ghost" size="icon" onClick={() => removeTeamMember(mIdx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div><Label className="text-xs">Name *</Label><Input value={member.name} onChange={(e) => updateTeamMember(mIdx, 'name', e.target.value)} placeholder="Name" className="text-sm" /></div>
                      <div><Label className="text-xs">Role</Label><Input value={member.role} onChange={(e) => updateTeamMember(mIdx, 'role', e.target.value)} placeholder="Role" className="text-sm" /></div>
                      <div><Label className="text-xs">Department</Label><Input value={member.department} onChange={(e) => updateTeamMember(mIdx, 'department', e.target.value)} placeholder="Dept" className="text-sm" /></div>
                      <div><Label className="text-xs">Years Exp</Label><Input type="number" value={member.years_experience} onChange={(e) => updateTeamMember(mIdx, 'years_experience', parseInt(e.target.value) || 0)} className="text-sm" /></div>
                    </div>

                    <div className="mt-3">
                      <Label className="text-xs font-medium">Skills Assessment</Label>
                      <div className="space-y-2 mt-2">
                        {member.skills.map((skill, sIdx) => (
                          <div key={sIdx} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end bg-white p-2 rounded border">
                            <div><Label className="text-[10px]">Skill</Label><Input value={skill.skill_name} onChange={(e) => updateSkill(mIdx, sIdx, 'skill_name', e.target.value)} placeholder="Skill" className="text-sm h-8" /></div>
                            <div><Label className="text-[10px]">Category</Label>
                              <Select value={skill.category} onValueChange={(v) => updateSkill(mIdx, sIdx, 'category', v)}>
                                <SelectTrigger className="text-sm h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>{formData.skill_categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div><Label className="text-[10px]">Current (0-5)</Label>
                              <Select value={skill.proficiency_level.toString()} onValueChange={(v) => updateSkill(mIdx, sIdx, 'proficiency_level', parseInt(v))}>
                                <SelectTrigger className="text-sm h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>{proficiencyLevels.map(l => <SelectItem key={l.value} value={l.value.toString()}>{l.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div><Label className="text-[10px]">Target (0-5)</Label>
                              <Select value={skill.target_level.toString()} onValueChange={(v) => updateSkill(mIdx, sIdx, 'target_level', parseInt(v))}>
                                <SelectTrigger className="text-sm h-8"><SelectValue /></SelectTrigger>
                                <SelectContent>{proficiencyLevels.map(l => <SelectItem key={l.value} value={l.value.toString()}>{l.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div><Label className="text-[10px]">Training Needed</Label><Input value={skill.training_needed} onChange={(e) => updateSkill(mIdx, sIdx, 'training_needed', e.target.value)} placeholder="Training" className="text-sm h-8" /></div>
                            <div className="flex justify-end"><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeSkill(mIdx, sIdx)}><Trash2 className="h-3 w-3 text-red-500" /></Button></div>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => addSkillToMember(mIdx)} className="mt-2"><Plus className="h-3 w-3 mr-1" /> Add Skill</Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <div><Label className="text-xs">Certifications</Label><Input value={member.certifications} onChange={(e) => updateTeamMember(mIdx, 'certifications', e.target.value)} placeholder="AWS, CKA, etc." className="text-sm" /></div>
                      <div><Label className="text-xs">Career Goals</Label><Input value={member.career_goals} onChange={(e) => updateTeamMember(mIdx, 'career_goals', e.target.value)} placeholder="Career aspirations" className="text-sm" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addTeamMember} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Team Member</Button>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Training Recommendations</Label>
              {formData.training_recommendations.map((rec, idx) => (
                <div key={idx} className="flex gap-2 mt-2">
                  <Input value={rec} onChange={(e) => {
                    const recs = [...formData.training_recommendations];
                    recs[idx] = e.target.value;
                    setFormData({...formData, training_recommendations: recs});
                  }} placeholder="Training recommendation" />
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, training_recommendations: formData.training_recommendations.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, training_recommendations: [...formData.training_recommendations, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SkillsMatrixForm;
